import React, { ComponentProps, useState } from 'react'
import { useRouter } from 'hooks/useRouter'
import { Button } from 'components/Button'
import { Icon } from 'components/Icon'
import { IconName } from 'components/Icon/iconNames'
import { Link } from 'components/Link'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { useTranslation } from 'hooks/useTranslation'
import { CollapsibleMenuRoot, MenuTrigger, MenuContent, LinkItem } from './CollapsibleMenu.styles'
import { MenuSection } from '../DesktopMenu/DesktopMenu'

type MenuOwnProps = ComponentProps<typeof CollapsibleMenuRoot>

export type CollapsibleMenuProps = MenuOwnProps &
  MenuSection & {
    label: string
    activeSection: string
  }

export const CollapsibleMenu = ({
  label,
  title,
  items,
  cta,
  activeSection,
}: CollapsibleMenuProps) => {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(true)
  const { asPath } = useRouter()
  const basePath = asPath.split('?')[0]

  const iconName: IconName = isOpen ? 'arrow-up-line' : 'arrow-down-line'

  return (
    <CollapsibleMenuRoot
      onOpenChange={setIsOpen}
      defaultOpen={true}
      css={
        activeSection === title
          ? {
              backgroundColor: '$primary-700',
              color: '$accent-white',
              border: '1px solid $primary-700',
            }
          : {}
      }
    >
      <MenuTrigger
        css={{
          borderColor: isOpen ? '$tertiary-1000' : 'transparent',
          borderRadius: isOpen ? '$4 $4 0 0' : '$4',
        }}
        active={activeSection === title}
      >
        <Text size="headline" weight="semiBold">
          {title}
        </Text>
        <Icon name={iconName} />
      </MenuTrigger>
      <MenuContent>
        <Stack as="nav" direction="column" gap="3" aria-label={label}>
          <Stack as="ul" role="list" direction="column">
            {items?.map(({ label, url }, index) => (
              <Link key={index} href={url} passHref skipParams={true}>
                <LinkItem active={url === basePath}>
                  <Text as="a" weight="bold">
                    {label}
                  </Text>
                </LinkItem>
              </Link>
            ))}
          </Stack>
          {cta && (
            <Button
              as="a"
              size="small"
              variant={cta.label === t('my-account:menu.pets.cta') ? 'primary' : 'secondary'}
              href={cta.url}
              css={{ marginBottom: '$1' }}
            >
              {cta.label}
            </Button>
          )}
        </Stack>
      </MenuContent>
    </CollapsibleMenuRoot>
  )
}

CollapsibleMenu.displayName = 'CollapsibleMenu'
